import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { AlertCircle, ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const UserProfileAlert = ({ userId }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkUserProfile = async () => {
            if (!userId) {
                navigate("/create-profile");
                return;
            }

            const cachedProfile = sessionStorage.getItem(`profile_${userId}`);
            const dismissed = sessionStorage.getItem(`profile_modal_dismissed_${userId}`);
            const userProfile = sessionStorage.getItem(`userProfile` || {});

            if (!userProfile || Object.keys(userProfile).length === 0) {
                sessionStorage.setItem(`profile_modal_dismissed_${userId}`, "false");
                setIsModalVisible(false);
                navigate("/create-profile");
                return;
            }

            if (dismissed === "true") return;

            if (cachedProfile !== null) {
                const profileExists = JSON.parse(cachedProfile);
                setIsModalVisible(!profileExists);
                return;
            }

            try {
                const response = await api.get(`/profile/${userId}`, {
                    headers: { "user-id": userId },
                });

                const profileExists = !!response.data;
                sessionStorage.setItem(`profile_${userId}`, JSON.stringify(profileExists));
                setIsModalVisible(!profileExists);
            } catch (error) {
                console.error("Error fetching profile:", error);
                sessionStorage.setItem(`profile_${userId}`, JSON.stringify(false));
                setIsModalVisible(true);
            }
        };

        if (userId) checkUserProfile();
    }, [userId, navigate]);

    const handleModalClose = () => {
        sessionStorage.setItem(`profile_modal_dismissed_${userId}`, "true");
        setIsModalVisible(false);
        navigate("/create-profile");
    };

    return (
        <AnimatePresence>
            {isModalVisible && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, y: 20 }}
                        className="bg-white rounded font-lora shadow-lg w-96 overflow-hidden border border-gray-100"
                    >
                        <div className="relative">
                            {/* Close button (optional) */}
                            <button
                                onClick={handleModalClose}
                                className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>

                            {/* Modal content */}
                            <div className="p-6 text-center">
                                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-50 mb-4">
                                    <AlertCircle className="h-8 w-8 text-blue-600" strokeWidth={1.5} />
                                </div>

                                <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wider">
                                    Profile Completion Required
                                </h3>
                                
                                <div className="text-sm text-gray-600 mb-6 space-y-2">
                                    <p>
                                        You are seeing this message because your profile registration is not complete.
                                    </p>
                                    <p>
                                        To access all platform features and connect with others, please complete your profile setup.
                                    </p>
                                </div>

                                <div className="flex justify-center">
                                    <motion.button
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={handleModalClose}
                                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded font-medium text-sm shadow-sm hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        Complete Profile
                                        <ArrowRight className="w-4 h-4" />
                                    </motion.button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default UserProfileAlert;